/**
 * School Mobile App Field Status API Contract
 *
 * GET /v1/schools/:school_id/mobile-app/field-status
 *
 * For every field a school edits in its app (U6's keys), whether what it saved
 * is live or a draft, and how a draft will reach families (release lines U7,
 * R11, R12, R15, KTD5). Read by the school editor; a super-admin passes the
 * same school gate for any school.
 *
 * PER PLATFORM, FROM THAT PLATFORM'S CURRENT LINE. iOS and Android sit on
 * different lines for the whole of a rollout, and whether a field is live
 * depends on the line (`school-app-field-delivery.ts`). Each field carries one
 * entry per platform the app answers for; `status` collapses them when every
 * platform agrees on the state, the class and the in-progress note, and is null
 * when they differ. A store field one store does not have (Play has no
 * keywords) has no entry for that platform.
 *
 * Supersedes `mobile-app-build-status` for the editor; that endpoint keeps
 * serving its current clients unchanged.
 */

import { z } from 'zod'
import {
  SchoolAppFieldDeliveryClassSchema,
  SchoolAppFieldDraftClassSchema
} from '../../schemas/entities/school-app-field-delivery'
import { SchoolAppFieldKeySchema } from '../../schemas/entities/school-app-field-draft'
import { SCHOOL_APP_BUILD_PLATFORMS } from './mobile-app-build-status'

export const SCHOOL_APP_FIELD_STATES = ['live', 'draft'] as const
export const SchoolAppFieldStateSchema = z.enum(SCHOOL_APP_FIELD_STATES)
export type SchoolAppFieldState = z.infer<typeof SchoolAppFieldStateSchema>

/**
 * One field's status, on one platform or collapsed over both.
 *
 *  - `deliveryClass`: how a change to this field reaches families on the line
 *    it is read from. A live field's class still says how the NEXT change will
 *    ship (a store field that matches its shipped text is `live` with class
 *    `store-listing`).
 *  - `inProgress`: a draft staff already released whose build or update has
 *    not reached families yet. Back to false if that release fails.
 *  - `savedAt`: the school's last save of this field, when one is known.
 *  - `releaseRequestedAt`: set only while the field is a draft (R15).
 *  - `publishedAt`: when what families have was published, when known. Null on
 *    a live field whose release left no record (a runtime field, a baseline).
 */
const FieldStatusDates = {
  inProgress: z.boolean(),
  savedAt: z.string().datetime().nullable(),
  releaseRequestedAt: z.string().datetime().nullable(),
  publishedAt: z.string().datetime().nullable()
}

/**
 * The state and the class, as a pair the type can hold to the rule: a `live`
 * class field is live on save, so a draft always carries a draft class (one of
 * `SCHOOL_APP_FIELD_DRAFT_LABELS`' keys). A live field may carry any class,
 * which says how the NEXT change will ship. "Draft, class live" is not a state
 * the derivation can produce, and this makes it unrepresentable rather than a
 * chip with no label.
 */
const liveState = { state: z.literal('live'), deliveryClass: SchoolAppFieldDeliveryClassSchema }
const draftState = { state: z.literal('draft'), deliveryClass: SchoolAppFieldDraftClassSchema }

const platformFields = {
  platform: z.enum(SCHOOL_APP_BUILD_PLATFORMS),
  /** The line the status is read from: the platform's current line, else its upcoming or the default line. */
  line: z.object({ id: z.string(), name: z.string(), runtimeVersion: z.string() }).nullable(),
  ...FieldStatusDates
}

export const SchoolAppFieldPlatformStatusSchema = z.discriminatedUnion('state', [
  z.object({ ...platformFields, ...liveState }),
  z.object({ ...platformFields, ...draftState })
])
export type SchoolAppFieldPlatformStatus = z.infer<typeof SchoolAppFieldPlatformStatusSchema>

export const SchoolAppFieldCollapsedStatusSchema = z.discriminatedUnion('state', [
  z.object({ ...FieldStatusDates, ...liveState }),
  z.object({ ...FieldStatusDates, ...draftState })
])
export type SchoolAppFieldCollapsedStatus = z.infer<typeof SchoolAppFieldCollapsedStatusSchema>

export const SchoolAppFieldStatusSchema = z.object({
  fieldKey: SchoolAppFieldKeySchema,
  /** One status when every platform agrees; null when they differ. */
  status: SchoolAppFieldCollapsedStatusSchema.nullable(),
  platforms: z.array(SchoolAppFieldPlatformStatusSchema)
})
export type SchoolAppFieldStatus = z.infer<typeof SchoolAppFieldStatusSchema>

/** GET response: every field in `SCHOOL_APP_FIELD_KEYS` order. */
export const SchoolAppFieldStatusResponseSchema = z.object({
  fields: z.array(SchoolAppFieldStatusSchema),
  /** True when any field is a draft on any platform. */
  hasPendingDrafts: z.boolean()
})
export type SchoolAppFieldStatusResponse = z.infer<typeof SchoolAppFieldStatusResponseSchema>
